import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Card,
  CardContent,
  Chip,
  Button,
  Divider,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  AttachMoney as MoneyIcon,
  Assessment as AssessmentIcon,
  GetApp as DownloadIcon,
} from '@mui/icons-material';

interface FinancialMetrics {
  totalRevenue: number;
  monthlyRevenue: number;
  averageInvoiceValue: number;
  outstandingAmount: number;
  revenueGrowth: number;
  collectionRate: number;
}

const FinancialReportsPage: React.FC = () => {
  const [metrics, setMetrics] = useState<FinancialMetrics>({
    totalRevenue: 0,
    monthlyRevenue: 0,
    averageInvoiceValue: 0,
    outstandingAmount: 0,
    revenueGrowth: 0,
    collectionRate: 0,
  });

  useEffect(() => {
    // Mock data - replace with actual API call
    setMetrics({
      totalRevenue: 248500,
      monthlyRevenue: 48500,
      averageInvoiceValue: 12150,
      outstandingAmount: 33500,
      revenueGrowth: 15.3,
      collectionRate: 94.2,
    });
  }, []);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const formatPercentage = (value: number) => {
    return `${value.toFixed(1)}%`;
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Financial Reports
        </Typography>
        <Button variant="outlined" startIcon={<DownloadIcon />}>
          Export Report
        </Button>
      </Box>

      {/* Key Metrics Cards */}
      <Box sx={{ display: 'flex', gap: 3, mb: 4, flexWrap: 'wrap' }}>
        <Card sx={{ flex: 1, minWidth: 250 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Total Revenue
                </Typography>
                <Typography variant="h4" component="div">
                  {formatCurrency(metrics.totalRevenue)}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                  <TrendingUpIcon color="success" sx={{ fontSize: 16, mr: 0.5 }} />
                  <Typography variant="body2" color="success.main">
                    +{formatPercentage(metrics.revenueGrowth)}
                  </Typography>
                </Box>
              </Box>
              <MoneyIcon color="primary" sx={{ fontSize: 40, opacity: 0.3 }} />
            </Box>
          </CardContent>
        </Card>

        <Card sx={{ flex: 1, minWidth: 250 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Monthly Revenue
                </Typography>
                <Typography variant="h4" component="div">
                  {formatCurrency(metrics.monthlyRevenue)}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Current month
                </Typography>
              </Box>
              <AssessmentIcon color="primary" sx={{ fontSize: 40, opacity: 0.3 }} />
            </Box>
          </CardContent>
        </Card>

        <Card sx={{ flex: 1, minWidth: 250 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Average Invoice Value
                </Typography>
                <Typography variant="h4" component="div">
                  {formatCurrency(metrics.averageInvoiceValue)}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Per invoice
                </Typography>
              </Box>
              <MoneyIcon color="primary" sx={{ fontSize: 40, opacity: 0.3 }} />
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* Outstanding & Collection Metrics */}
      <Box sx={{ display: 'flex', gap: 3, mb: 4, flexWrap: 'wrap' }}>
        <Card sx={{ flex: 1, minWidth: 300 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Outstanding Amount
            </Typography>
            <Typography variant="h4" component="div" color="warning.main">
              {formatCurrency(metrics.outstandingAmount)}
            </Typography>
            <Divider sx={{ my: 2 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" color="text.secondary">
                Current Outstanding
              </Typography>
              <Chip 
                label="Needs Attention" 
                color="warning" 
                size="small" 
              />
            </Box>
          </CardContent>
        </Card>

        <Card sx={{ flex: 1, minWidth: 300 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Collection Rate
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant="h4" component="div" color="success.main">
                {formatPercentage(metrics.collectionRate)}
              </Typography>
              <TrendingUpIcon color="success" sx={{ ml: 1 }} />
            </Box>
            <Divider sx={{ my: 2 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" color="text.secondary">
                Above Industry Average
              </Typography>
              <Chip 
                label="Excellent" 
                color="success" 
                size="small" 
              />
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* Revenue Trends */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Revenue Analysis
        </Typography>
        <Box sx={{ mt: 2 }}>
          <Typography variant="body1" paragraph>
            <strong>Revenue Growth:</strong> Your business is showing a healthy growth rate of {formatPercentage(metrics.revenueGrowth)} 
            compared to the previous period, indicating strong market performance and client satisfaction.
          </Typography>
          <Typography variant="body1" paragraph>
            <strong>Collection Performance:</strong> With a collection rate of {formatPercentage(metrics.collectionRate)}, 
            your payment collection is performing above industry standards (typically 85-90%).
          </Typography>
          <Typography variant="body1" paragraph>
            <strong>Outstanding Management:</strong> Current outstanding amount of {formatCurrency(metrics.outstandingAmount)} 
            represents approximately {((metrics.outstandingAmount / metrics.totalRevenue) * 100).toFixed(1)}% of total revenue, 
            which is within acceptable limits.
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
};

export default FinancialReportsPage;